import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  useWebsiteAnnouncementOptional,
  useWebsiteUpdateAnnouncement,
  useWebsiteUploadBlogInlineImage,
} from "@/queries/website";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RichDocumentEditor } from "@/components/ui/rich-document-editor";
import { Megaphone } from "lucide-react";

const EMPTY_BODY = JSON.stringify({ type: "doc", content: [{ type: "paragraph" }] });

export function AnnouncementPanel() {
  const { data: announcement, isLoading } = useWebsiteAnnouncementOptional();
  const updateMutation = useWebsiteUpdateAnnouncement();
  const uploadInlineImageMutation = useWebsiteUploadBlogInlineImage();

  const [active, setActive] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState(EMPTY_BODY);
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");

  useEffect(() => {
    if (announcement) {
      setActive(announcement.active);
      setTitle(announcement.title ?? "");
      setBody(announcement.body ?? EMPTY_BODY);
      setCtaLabel(announcement.cta_label ?? "");
      setCtaUrl(announcement.cta_url ?? "");
    }
  }, [announcement]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      await updateMutation.mutateAsync({
        active,
        title: title.trim() || null,
        body,
        cta_label: ctaLabel.trim() || null,
        cta_url: ctaUrl.trim() || null,
      });
      toast.success("Announcement saved.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save announcement.");
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="size-5" />
            Home page announcement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Show a one-time pop-up on the public home page. Visitors see it once; it reappears for
            everyone whenever you save a change here. Turn it off with the Active switch.
          </p>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-4 max-w-2xl">
              <div className="flex items-center gap-3">
                <Switch id="active" checked={active} onCheckedChange={setActive} />
                <Label htmlFor="active">Active</Label>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Announcement title"
                />
              </div>
              <div className="grid gap-2">
                <Label>Content</Label>
                <RichDocumentEditor
                  value={body}
                  onChange={setBody}
                  placeholder="Write your announcement…"
                  stickyToolbar
                  onImageUpload={(base64) =>
                    uploadInlineImageMutation.mutateAsync(base64).then((r) => r.url)
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="cta_label">Call-to-action button label</Label>
                <Input
                  id="cta_label"
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  placeholder="e.g. Register now"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="cta_url">Call-to-action button URL</Label>
                <Input
                  id="cta_url"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  placeholder="/events or https://…"
                />
              </div>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving…" : "Save announcement"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
